// -----------------------------------------------------------
// FloraIQ — Tabela de Inconsistências (dado real de GET /quality/issues)
// -----------------------------------------------------------

import { useMemo, useState } from "react";
import type { Inconsistency } from "@/types/ui-models";
import { NoRealData } from "@/components/shared";
import { QUALITY_RULES_BY_CODE, QUALITY_SEVERITY_OPTIONS } from "@/config/qualidade-3d-config";
import { SeverityBadge, ValidationStatusBadge } from "./QualityStatusBadge";
import InconsistencyDetailDrawer from "./InconsistencyDetailDrawer";
import { formatDetectedAt, projectLabel } from "./qualityFormat";

interface InconsistenciesTableProps {
  issues: Inconsistency[];
  loading: boolean;
  error: string | null;
  projectNames: Record<string, string>;
  /** Restringe a tabela a estas regras (abas por grupo). */
  ruleCodes?: string[];
  emptyTitle?: string;
}

export default function InconsistenciesTable({
  issues,
  loading,
  error,
  projectNames,
  ruleCodes,
  emptyTitle = "Nenhuma inconsistência aberta",
}: InconsistenciesTableProps) {
  const [search, setSearch] = useState("");
  const [filterSeverity, setFilterSeverity] = useState("");
  const [selected, setSelected] = useState<Inconsistency | null>(null);

  const scoped = useMemo(
    () => (ruleCodes ? issues.filter((i) => ruleCodes.includes(i.regra)) : issues),
    [issues, ruleCodes]
  );

  const filtered = scoped.filter((inc) => {
    const q = search.toLowerCase();
    if (q) {
      const ruleName = QUALITY_RULES_BY_CODE[inc.regra]?.name ?? "";
      const haystack = [inc.regra, ruleName, inc.descricao, projectLabel(inc.projeto, projectNames)]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    if (filterSeverity && inc.severidade !== filterSeverity) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-foreground-500 py-10 justify-center">
        <i className="ri-loader-4-line animate-spin"></i> Carregando inconsistências...
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50/60 border border-red-200/60 rounded-card p-4 text-xs text-red-700">
        Não foi possível carregar as inconsistências: {error}
      </div>
    );
  }

  if (scoped.length === 0) {
    return (
      <NoRealData
        title={emptyTitle}
        icon="ri-checkbox-circle-line"
        description="As regras de qualidade implementadas no banco não encontraram registros inconsistentes neste momento."
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            placeholder="Buscar por regra, projeto ou descrição..."
            className="w-full border border-background-200/70 rounded-md pl-9 pr-3 py-2 text-sm text-foreground-800 focus:outline-none focus:border-blue-400 transition-colors"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="border border-background-200/70 rounded-md px-3 py-2 text-sm text-foreground-800 focus:outline-none focus:border-blue-400 transition-colors"
          value={filterSeverity}
          onChange={(e) => setFilterSeverity(e.target.value)}
        >
          <option value="">Todas severidades</option>
          {QUALITY_SEVERITY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      <p className="text-xs text-foreground-500">{filtered.length} inconsistências encontradas</p>

      {/* Tabela */}
      <div className="overflow-x-auto border border-background-200/70 rounded-card">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-background-50 border-b border-background-200/70">
              <th className="text-left px-3 py-2.5 font-medium text-foreground-600 whitespace-nowrap w-10"></th>
              <th className="text-left px-3 py-2.5 font-medium text-foreground-600 whitespace-nowrap">Regra</th>
              <th className="text-left px-3 py-2.5 font-medium text-foreground-600 whitespace-nowrap">Projeto</th>
              <th className="text-left px-3 py-2.5 font-medium text-foreground-600 whitespace-nowrap">Descrição</th>
              <th className="text-left px-3 py-2.5 font-medium text-foreground-600 whitespace-nowrap">Detectada em</th>
              <th className="text-left px-3 py-2.5 font-medium text-foreground-600 whitespace-nowrap">Status</th>
              <th className="text-center px-3 py-2.5 font-medium text-foreground-600 whitespace-nowrap">Detalhe</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((inc) => (
              <tr key={inc.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                <td className="px-3 py-2.5"><SeverityBadge severity={inc.severidade} /></td>
                <td className="px-3 py-2.5 text-foreground-600 max-w-[220px] truncate" title={inc.regra}>
                  {QUALITY_RULES_BY_CODE[inc.regra]?.name ?? inc.regra}
                </td>
                <td className="px-3 py-2.5 text-foreground-600 max-w-[160px] truncate">{projectLabel(inc.projeto, projectNames)}</td>
                <td className="px-3 py-2.5 text-foreground-700 max-w-[280px] truncate">{inc.descricao}</td>
                <td className="px-3 py-2.5 text-foreground-500 whitespace-nowrap">{formatDetectedAt(inc.detectadaEm)}</td>
                <td className="px-3 py-2.5"><ValidationStatusBadge status={inc.status} /></td>
                <td className="px-3 py-2.5 text-center">
                  <button
                    onClick={() => setSelected(inc)}
                    className="w-7 h-7 rounded-lg inline-flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer"
                    title="Abrir detalhe"
                  >
                    <i className="ri-eye-line text-foreground-500 text-sm"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <InconsistencyDetailDrawer
        issue={selected}
        projectNames={projectNames}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}
